import React, { useState, useEffect, useCallback } from 'react';
import socket from '../socket';
import PokemonCard from './PokemonCard';
import QuestionLog from './QuestionLog';
import SecretPokemon from './SecretPokemon';
import TypeLegend from './TypeLegend';
import Confetti from './Confetti';

function Game({ gameState, disconnected, onLeave }) {
  const { players, secretPokemon, board, playerId, roomCode, boardSize } = gameState;
  const [currentTurn, setCurrentTurn] = useState(gameState.currentTurn);
  const [eliminated, setEliminated] = useState(new Set());
  const [question, setQuestion] = useState('');
  const [log, setLog] = useState([]);
  const [pendingQuestion, setPendingQuestion] = useState(null);
  const [opponentRemaining, setOpponentRemaining] = useState(boardSize || 24);
  const [gameOver, setGameOver] = useState(null);
  const [guessMode, setGuessMode] = useState(false);
  const [awaitingAnswer, setAwaitingAnswer] = useState(false);
  const [rejectedMsg, setRejectedMsg] = useState('');

  useEffect(() => {
    socket.on('question-asked', ({ question, asker, askerId }) => {
      if (askerId !== playerId) {
        setPendingQuestion({ question, asker });
      }
      if (askerId === playerId) {
        setAwaitingAnswer(true);
      }
      setLog((prev) => [...prev, { question, asker }]);
    });

    socket.on('question-answered', ({ question, answer, asker }) => {
      setAwaitingAnswer(false);
      setPendingQuestion(null);
      setLog((prev) => {
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].question === question && updated[i].answer === undefined) {
            updated[i] = { ...updated[i], answer };
            break;
          }
        }
        return updated;
      });
    });

    socket.on('turn-changed', ({ currentTurn }) => {
      setCurrentTurn(currentTurn);
      setGuessMode(false);
      setPendingQuestion(null);
      setAwaitingAnswer(false);
    });

    socket.on('opponent-eliminated', ({ remainingCount }) => {
      setOpponentRemaining(remainingCount);
    });

    socket.on('game-over', (data) => {
      setGameOver(data);
    });

    socket.on('question-rejected', ({ reason }) => {
      setRejectedMsg(reason);
      setTimeout(() => setRejectedMsg(''), 4000);
    });

    return () => {
      socket.off('question-asked');
      socket.off('question-answered');
      socket.off('turn-changed');
      socket.off('opponent-eliminated');
      socket.off('game-over');
      socket.off('question-rejected');
    };
  }, [playerId]);

  const isMyTurn = currentTurn === playerId;
  const myName = players.find(p => p.id === playerId)?.name || 'You';
  const opponentName = players.find(p => p.id !== playerId)?.name || 'Opponent';
  const totalCards = boardSize || 24;

  const handleAsk = useCallback(() => {
    if (!question.trim() || !isMyTurn || awaitingAnswer) return;
    setRejectedMsg('');
    socket.emit('ask-question', { roomCode: roomCode, question: question.trim() });
    setQuestion('');
  }, [question, isMyTurn, roomCode, awaitingAnswer]);

  const handleAnswer = (answer) => {
    socket.emit('answer-question', { roomCode: roomCode, answer });
    setPendingQuestion(null);
  };

  const handleCardClick = (pokemon) => {
    if (gameOver) return;

    if (guessMode) {
      socket.emit('make-guess', { roomCode: roomCode, pokemonId: pokemon.id });
      setGuessMode(false);
      return;
    }

    setEliminated((prev) => {
      const next = new Set(prev);
      if (next.has(pokemon.id)) {
        next.delete(pokemon.id);
      } else {
        next.add(pokemon.id);
      }
      socket.emit('eliminate-pokemon', { roomCode: roomCode, pokemonId: pokemon.id });
      return next;
    });
  };

  const toggleGuessMode = () => setGuessMode(!guessMode);

  if (gameOver) {
    const isWinner = gameOver.winner === playerId;
    const guesserName = gameOver.guesser === playerId ? myName : opponentName;
    return (
      <div className="game-over-screen">
        {isWinner && <Confetti />}
        <h1>{isWinner ? 'You Win!' : 'You Lose!'}</h1>
        <p>
          {guesserName} guessed <strong>{gameOver.guessedPokemon.name}</strong>
          {gameOver.correct ? ' correctly!' : ' incorrectly!'}
        </p>
        <div className="game-over-secrets">
          {players.map((p) => (
            <div key={p.id} className="secret-reveal">
              <p>{p.id === playerId ? 'Your' : `${opponentName}'s`} secret:</p>
              <img
                src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${gameOver.secrets[p.id].id}.png`}
                alt={gameOver.secrets[p.id].name}
                className="secret-reveal-sprite"
              />
              <strong>{gameOver.secrets[p.id].name}</strong>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="btn btn-create"
          onClick={onLeave}
          onTouchEnd={(e) => { e.preventDefault(); onLeave(); }}
        >
          Back to Lobby
        </button>
      </div>
    );
  }

  if (disconnected) {
    return (
      <div className="game-over-screen">
        <h1>Opponent Disconnected</h1>
        <p>Your opponent left the game.</p>
        <button
          type="button"
          className="btn btn-create"
          onClick={onLeave}
          onTouchEnd={(e) => { e.preventDefault(); onLeave(); }}
        >
          Back to Lobby
        </button>
      </div>
    );
  }

  return (
    <div className="game">
      <div className="game-header">
        <div className="turn-indicator">
          {isMyTurn ? "Your Turn" : `${opponentName}'s Turn`}
        </div>
        <div className="game-info">
          <span>You: {totalCards - eliminated.size} left</span>
          <span>{opponentName}: {opponentRemaining} left</span>
        </div>
      </div>

      <div className="game-layout">
        <div className="board-section">
          <div className={`pokemon-board${guessMode ? ' guess-mode' : ''}${totalCards === 36 ? ' board-36' : ''}`}>
            {board.map((pokemon) => (
              <PokemonCard
                key={pokemon.id}
                pokemon={pokemon}
                eliminated={eliminated.has(pokemon.id)}
                onClick={() => handleCardClick(pokemon)}
                selectable={guessMode && !eliminated.has(pokemon.id)}
              />
            ))}
          </div>
        </div>

        <div className="sidebar">
          <SecretPokemon pokemon={secretPokemon} />
          <TypeLegend />

          <div className="controls">
            {isMyTurn && !awaitingAnswer && !pendingQuestion && (
              <div className="question-input">
                <input
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAsk()}
                  placeholder="Ask a yes/no question..."
                  maxLength={200}
                />
                <button
                  type="button"
                  className="btn btn-ask"
                  onClick={handleAsk}
                  onTouchEnd={(e) => { e.preventDefault(); handleAsk(); }}
                >
                  Ask
                </button>
              </div>
            )}

            {rejectedMsg && (
              <p className="error-message" style={{ marginTop: 8 }}>{rejectedMsg}</p>
            )}

            {awaitingAnswer && (
              <p className="waiting-text">Waiting for answer...</p>
            )}

            {pendingQuestion && !isMyTurn && (
              <div className="answer-section">
                <p className="pending-question">
                  <strong>{pendingQuestion.asker} asks:</strong><br />
                  "{pendingQuestion.question}"
                </p>
                <div className="answer-buttons">
                  <button
                    type="button"
                    className="btn btn-yes"
                    onClick={() => handleAnswer('yes')}
                    onTouchEnd={(e) => { e.preventDefault(); handleAnswer('yes'); }}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className="btn btn-sometimes"
                    onClick={() => handleAnswer('sometimes')}
                    onTouchEnd={(e) => { e.preventDefault(); handleAnswer('sometimes'); }}
                  >
                    Sometimes
                  </button>
                  <button
                    type="button"
                    className="btn btn-no"
                    onClick={() => handleAnswer('no')}
                    onTouchEnd={(e) => { e.preventDefault(); handleAnswer('no'); }}
                  >
                    No
                  </button>
                </div>
              </div>
            )}

            {isMyTurn && !awaitingAnswer && (
              <div className="turn-actions">
                <button
                  type="button"
                  className={`btn ${guessMode ? 'btn-cancel' : 'btn-guess'}`}
                  onClick={toggleGuessMode}
                  onTouchEnd={(e) => { e.preventDefault(); toggleGuessMode(); }}
                >
                  {guessMode ? 'Cancel Guess' : 'Make a Guess'}
                </button>
              </div>
            )}

            {guessMode && (
              <p className="guess-hint">Click a Pokemon to guess!</p>
            )}
          </div>

          <QuestionLog log={log} />
        </div>
      </div>
    </div>
  );
}

export default Game;
